import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { observer, inject } from 'mobx-react'
import { useEffect } from 'react'

import { HelpIcon, TooltipText } from '../../../components'
import { MOBILE } from '../../../guided-toolbox/utils/breakpoints'
import { Page } from '../../retail/elements'
import {
  formatAmount,
  intervalAbbrev,
  intervalWord,
  priceSubtext,
} from '../../retail/retail-onboarding/pricebook-display'
import { useRetailCheckout } from '../../retail/retail-onboarding/useRetailCheckout'
import { CheckoutError, PriceRow } from '../../home/retail-upsell/styles'
import ProfileCard from '../ProfileCard'

const ServiceRow = styled.div`
  font-size: 1rem;
  color: #022a3a;
  line-height: 1.35;
  margin: 12px 0;
  display: flex;
  flex-wrap: wrap;

  /* Basis 400px keeps label and value side by side on wide screens and wraps
     them onto their own lines below it. Shrink stays enabled so that on a phone,
     where the card is narrower than the basis, the columns collapse to the card
     width instead of overflowing it. */
  & > div:nth-of-type(1),
  & > div:nth-of-type(2) {
    flex: 1 1 400px;
    min-width: 0;
    overflow-wrap: break-word;
  }
`

const ServiceButton = styled.button`
  color: #038ab7;
  text-decoration: underline;
  background-color: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
`

// Match the label and value columns used by the other Profile cards.
const SubscribeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-areas:
    'label status'
    'price info';
  align-items: start;
  row-gap: 12px;
  margin: 12px 0;
  color: #022a3a;
  font-size: 1rem;
  line-height: 1.35;

  ${MOBILE} {
    grid-template-columns: 1fr;
    grid-template-areas:
      'label'
      'price'
      'status'
      'info';
    row-gap: 8px;
    margin-top: 0;
  }
`

const SubscribeLabel = styled.div`
  grid-area: label;
`

const SubscribeStatus = styled.div`
  grid-area: status;
  padding-left: 8px;

  ${MOBILE} {
    padding-left: 0;
    margin-top: 20px;
  }
`

const PriceBlock = styled.div`
  grid-area: price;
  display: flex;
  flex-direction: column;
`

const CardPrice = styled(PriceRow)`
  margin-top: 0;
  justify-content: flex-start;
`

const Disclosure = styled.div`
  grid-area: info;
  max-width: 430px;
  padding-left: 8px;
  color: ${p => p.theme.lightestGray};
  font-size: 12px;
  line-height: 16px;

  ${MOBILE} {
    max-width: none;
    padding-left: 0;
    margin-top: 20px;
  }
`

const SubscribeError = styled(CheckoutError)`
  grid-column: 1 / -1;
`

const SecureCheckoutButton = styled(Page.PlanCta)`
  flex: none;
  width: 172px;
  height: 34px;
  margin: 0;

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }

  ${MOBILE} {
    order: 1;
    width: 100%;
    margin-top: 20px;
  }
`

const SUBSCRIPTION_LABEL = 'Advisory services subscription'

function ServicesCard(props) {
  const {
    person,
    account,
    features,
    config,
    getDefaultPayment,
    defaultPaymentInfo,
    subscription,
    subscriptionStatus,
    subscriptionExpiration,
    subscriptionsLoaded,
    createRetailCheckoutSession,
  } = props.store
  const { isRetail } = props

  // subscription is null both before the initial fetch resolves and for a
  // member with no subscription record — gate on subscriptionsLoaded so a
  // slow load doesn't flash "Not subscribed" for an active/expired member.
  const isLoadingSubscription = !subscriptionsLoaded
  const isNotSubscribed = subscriptionsLoaded && !subscription
  const isActive = subscriptionStatus === 'active' || subscriptionStatus === 'trialing'
  const isCancelled = subscriptionStatus === 'cancelled' || subscriptionStatus === 'canceled'
  const isExpired = subscriptionStatus === 'expired'

  // Resubscribe only works for member-paid Stripe subscriptions: the reactivate
  // endpoint needs a pricebookId, which the backend only sets for Stripe origins,
  // and org-paid members must not be able to start a personal charge.
  const canResubscribe =
    (isExpired || isCancelled) &&
    subscription?.origin === 'STRIPE' &&
    subscription?.paidBy !== 'ORG' &&
    Boolean(subscription?.pricebookId)

  const { offer, offerUnavailable, termsOfServiceLink, checkoutStatus, startCheckout } =
    useRetailCheckout(createRetailCheckoutSession, { enabled: isRetail && isNotSubscribed })

  useEffect(() => {
    getDefaultPayment()
  }, [getDefaultPayment])

  if (
    !features.managedAccountAvailable &&
    !features.autoRiskAdjustEnabled &&
    !features.enablePersonalizedTargetDatePlus &&
    !isRetail
  ) {
    return null
  }

  const price = offer?.prices?.[0]

  const termsOfService = termsOfServiceLink ? (
    <Page.Link href={termsOfServiceLink} target='_blank' rel='noopener noreferrer'>
      Terms of Service
    </Page.Link>
  ) : (
    'Terms of Service'
  )

  const secureCheckoutAction =
    isNotSubscribed && price ? (
      <SecureCheckoutButton
        type='button'
        disabled={checkoutStatus === 'pending'}
        onClick={startCheckout}>
        Secure checkout
      </SecureCheckoutButton>
    ) : undefined

  const notSubscribedSection = (
    <SubscribeGrid>
      <SubscribeLabel>{SUBSCRIPTION_LABEL}</SubscribeLabel>
      <SubscribeStatus>Not subscribed</SubscribeStatus>
      {price && (
        <>
          <PriceBlock>
            <CardPrice>
              <span className='currency'>$</span>
              <span className='amount'>{formatAmount(price.price / 100)}</span>
              <span className='interval'>/{intervalAbbrev[price.intervalType]}</span>
            </CardPrice>
            <Page.PlanPriceNote>{priceSubtext(offer)}</Page.PlanPriceNote>
          </PriceBlock>
          <Disclosure>
            If started, this subscription automatically renews every{' '}
            {intervalWord(price.intervalType)} and will continue to auto-renew until you cancel.
            Please see our {termsOfService} and{' '}
            <Page.Link
              href='/retail/payment/cancellation-policy'
              target='_blank'
              rel='noopener noreferrer'>
              Cancellation and Refund Policy
            </Page.Link>{' '}
            for details.
          </Disclosure>
        </>
      )}
      {(checkoutStatus === 'failed' || offerUnavailable) && (
        <SubscribeError role='alert'>
          {offerUnavailable
            ? 'Subscription pricing is unavailable right now. Please call (800) 774-7459 or email help@guidedchoice.com.'
            : "We couldn't start checkout just now. Please try again."}
        </SubscribeError>
      )}
    </SubscribeGrid>
  )

  return isRetail ? (
    <ProfileCard title='GuidedChoice services' headerAction={secureCheckoutAction}>
      {isNotSubscribed ? (
        notSubscribedSection
      ) : (
        <ServiceRow>
          <div>{SUBSCRIPTION_LABEL}</div>
          <div
            css={css`
              padding-left: 8px;
            `}>
            {isLoadingSubscription ? null : isActive ? (
              <>
                Active (automatic renewal on {subscriptionExpiration})
                {defaultPaymentInfo.lastDigits && (
                  <>
                    —<ServiceButton onClick={props.showDiscontinueRetail}>Edit</ServiceButton>
                  </>
                )}
              </>
            ) : isCancelled ? (
              <>
                Canceled (subscription ends on {subscriptionExpiration})
                {canResubscribe && (
                  <>
                    —<ServiceButton onClick={props.showReinstateRetail}>Edit</ServiceButton>
                  </>
                )}
              </>
            ) : isExpired ? (
              <>
                Expired (subscription ended on {subscriptionExpiration})
                {canResubscribe && (
                  <>
                    —<ServiceButton onClick={props.showReinstateRetail}>Edit</ServiceButton>
                  </>
                )}
              </>
            ) : null}
          </div>
        </ServiceRow>
      )}
    </ProfileCard>
  ) : (
    <ProfileCard title='GuidedChoice services'>
      <div
        css={css`
          color: #7a8e96;
          font-size: 18px;
          line-height: 1.35;
          margin: 0 0 1.75rem;
        `}>
        {person.displayName}'s investment plan account with {config.template.sponsorName}
      </div>

      {features.managedAccountAvailable && (
        <ServiceRow>
          <div>
            {config.template.managedAccountType}{' '}
            <HelpIcon
              size='mediumLarge'
              tooltip={
                config.template.managedAccountType === 'Managed account'
                  ? TooltipText.managedAccount()
                  : TooltipText.pointInTimeAdvice()
              }
            />
          </div>
          <div
            css={css`
              padding: 0 20px;
            `}>
            {account.managedAccount ? 'Active—' : 'Not enrolled'}
            {account.managedAccount && (
              <ServiceButton onClick={props.showDiscontinue}>Discontinue</ServiceButton>
            )}
          </div>
        </ServiceRow>
      )}

      {features.autoRiskAdjustEnabled && (
        <ServiceRow>
          <div>
            Automatic risk level adjustment{' '}
            <HelpIcon size='mediumLarge' tooltip={TooltipText.autoRiskAdjustment()} />
          </div>
          <div
            css={css`
              padding: 0 20px;
            `}>
            {account.autoRiskLevelAdjustmentStatus ? 'Include' : 'Exclude'}—
            <ServiceButton onClick={props.showAutoRiskLevelAdjust}>Edit</ServiceButton>
          </div>
        </ServiceRow>
      )}

      {features.enablePersonalizedTargetDatePlus && (
        <ServiceRow>
          <div>
            Personalized Target Date+{' '}
            <HelpIcon size='mediumLarge' tooltip={TooltipText.personalizedTargetDatePlus()} />
          </div>
          <div
            css={css`
              padding: 0 20px;
            `}>
            {account.managedAccount ? 'Active—' : 'Not enrolled'}
            {account.managedAccount && (
              <ServiceButton onClick={props.showDiscontinuePTDPlus}>Discontinue</ServiceButton>
            )}
          </div>
        </ServiceRow>
      )}
    </ProfileCard>
  )
}

export default inject('store')(observer(ServicesCard))
